import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyButtons from "@/components/StickyButtons";
import Reveal from "@/components/Reveal";
import RequireAdmin from "@/components/RequireAdmin";
import { AuthProvider } from "@/lib/auth";

import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";
import JoinTalentPoolPage from "@/pages/JoinTalentPoolPage";
import JobDetailPage from "@/pages/JobDetailPage";
import LoginPage from "@/pages/LoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import FreelancerDashboardPage from "@/pages/FreelancerDashboardPage";
import RequireFreelancer from "@/components/RequireFreelancer";
import { Navigate } from "react-router-dom";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function NotFound() {
  return (
    <section className="min-h-[60vh] flex items-center justify-center px-6 pt-32">
      <div className="text-center max-w-md">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">404</p>
        <h1 className="mt-3 font-heading font-extrabold text-4xl text-slate-900">Page not found</h1>
        <p className="mt-3 text-slate-600">The page you're looking for doesn't exist.</p>
        <a href="/" className="mt-6 inline-flex px-5 py-2.5 rounded-lg bg-primary text-white font-semibold">Back to home</a>
      </div>
    </section>
  );
}

function PublicShell({ children }) {
  return (
    <>
      <Reveal />
      <Navbar />
      <main>{children}</main>
      <Footer />
      <StickyButtons />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/admin" element={<RequireAdmin><AdminDashboardPage /></RequireAdmin>} />
      <Route path="/freelancer" element={<RequireFreelancer><FreelancerDashboardPage /></RequireFreelancer>} />

      <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
      <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
      <Route path="/services" element={<PublicShell><ServicesPage /></PublicShell>} />
      <Route path="/careers" element={<PublicShell><CareersPage /></PublicShell>} />
      <Route path="/jobs/:id" element={<PublicShell><JobDetailPage /></PublicShell>} />
      <Route path="/join" element={<PublicShell><JoinTalentPoolPage /></PublicShell>} />
      <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />
      <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App bg-white text-slate-900">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
